import React from 'react';
import Link from 'next/link';
import { withTranslation } from 'next-i18next';
import appUtils from '../../../../utils/appUtils';

interface FooterWithContentsProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  currentLocale: string;
}

const FooterWithContents: React.FC<FooterWithContentsProps> = (props) => {
  const { currentLocale, t } = props;
  const language = appUtils.getLangFromLocale(currentLocale);
  const baseUrl = appUtils.getSiteUrl(language);
  let tradeInJapan: React.ReactNode = null;
  let facebookLink = 'https://www.facebook.com/lingualbox';
  let twitterLink = 'https://twitter.com/lingualbox';
  let termsLink = `${baseUrl}/terms`;
  let privacyLink = `${baseUrl}/privacy`;
  let blog: React.ReactNode = null;
  if (currentLocale === 'ja') {
    tradeInJapan = (
      <li>
        <Link href="/trade">特商法に基く表記</Link>
      </li>
    );
    blog = (
      <li>
        <a href="http://ja.lingualbox.com/blog">ブログ</a>
      </li>
    );
    termsLink = `${baseUrl}/terms-ja`;
    privacyLink = `${baseUrl}/privacy-ja`;
    facebookLink = 'https://www.facebook.com/lingualbox.jp';
    twitterLink = 'https://twitter.com/lingualbox_jp';
  } else if (currentLocale === 'tw') {
    facebookLink = 'https://www.facebook.com/lingualbox.com.tw';
    twitterLink = 'https://twitter.com/lingualbox_tw';
  } else if (currentLocale === 'es') {
    facebookLink =
      'https://www.facebook.com/LingualBox-Aprender-a-hablar-inglés-online-524980884352619';
    twitterLink = 'https://twitter.com/lingualbox_es';
  } else if (currentLocale === 'pt') {
    facebookLink = 'https://www.facebook.com/lingualbox.com.br';
    twitterLink = 'https://twitter.com/lingualbox_br';
  } else if (currentLocale === 'ru') {
    twitterLink = 'https://twitter.com/lingualbox_ru';
  } else {
    blog = (
      <li>
        <a href="https://www.lingualbox.com/blog/">Blog</a>
      </li>
    );
  }
  let comparisonContent: React.ReactNode = <div />;
  if (currentLocale === 'ja') {
    comparisonContent = (
      <div>
        <h4 className="font-medium text-[17px] text-white">比較する</h4>
        <ul className="[&_li]:leading-loose [&_li]:text-[15px] [&_li]:font-normal [&_a]:text-slate-400 [&_a]:transition-colors [&_a]:duration-300 [&_a:hover]:text-white">
          <li>
            <Link
              href={`${baseUrl}/comparison/${'personal-tutor-vs-online-school'}`}
            >
              個人英会話講師との比較
            </Link>
          </li>
          <li>
            <Link href={`${baseUrl}/comparison/${'learn-in-the-philippines'}`}>
              フィリピン留学との比較
            </Link>
          </li>
          <li>
            <Link href={`${baseUrl}/comparison/${'tickets-vs-daily'}`}>
              毎日プラン型サービスとの比較
            </Link>
          </li>
          <li>
            <Link
              href={`${baseUrl}/comparison/${'online-school-vs-english-bootcamp'}`}
            >
              英語学習ブートキャンプとの比較
            </Link>
          </li>
          <li>
            <Link href={`${baseUrl}/comparison/${'one-to-one-vs-group'}`}>
              グループレッスンとの比較
            </Link>
          </li>
          <li>
            <Link href={`${baseUrl}/comparison/${'offline-english-school'}`}>
              通学型英会話教室との比較
            </Link>
          </li>
          <li>
            <Link href={`${baseUrl}/comparison/${'language-exchange'}`}>
              ランゲージ・エクスチェンジとの比較
            </Link>
          </li>
        </ul>
      </div>
    );
  }
  return (
    <footer className="bg-slate-800 py-8 sm-center">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3 mb-4">
            <h4 className="font-medium text-[17px] text-white">{t('service.footer.site')}</h4>
            <ul className="[&_li]:leading-loose [&_li]:text-[15px] [&_li]:font-normal [&_a]:text-slate-400 [&_a]:transition-colors [&_a]:duration-300 [&_a:hover]:text-white">
              <li>
                <Link href={`${baseUrl}`}>{t('links.home')}</Link>
              </li>
              <li>
                <Link href={`${baseUrl}/school/learn-spoken-english`}>
                  {t('links.school')}
                </Link>
              </li>
              <li>
                <Link href="/tutors">{t('links.tutors')}</Link>
              </li>
              <li>
                <Link href={`${baseUrl}/courses`}>{t('links.courses')}</Link>
              </li>
              <li>
                <Link href={`${baseUrl}/pricing`}>{t('links.pricing')}</Link>
              </li>
            </ul>
            <div className="mt-4 sm:mb-4">
              <h4 className="font-medium text-[17px] text-white">{t('service.footer.support')}</h4>
              <ul className="[&_li]:leading-loose [&_li]:text-[15px] [&_li]:font-normal [&_a]:text-slate-400 [&_a]:transition-colors [&_a]:duration-300 [&_a:hover]:text-white">
                <li>
                  <Link href={`${baseUrl}/faq`}>{t('links.faq')}</Link>
                </li>
                <li>
                  <a href="mailto:support@lingualbox.com">
                    support@lingualbox.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-span-3 mb-4">
            <h4 className="font-medium text-[17px] text-white">{t('service.footer.legal')}</h4>
            <ul className="[&_li]:leading-loose [&_li]:text-[15px] [&_li]:font-normal [&_a]:text-slate-400 [&_a]:transition-colors [&_a]:duration-300 [&_a:hover]:text-white">
              <li>
                <Link href={termsLink}>{t('links.terms')}</Link>
              </li>
              <li>
                <Link href={privacyLink}>{t('links.privacy')}</Link>
              </li>
              {tradeInJapan}
            </ul>
          </div>
          <div className="col-span-3 mb-4 hidden md:block">
            {comparisonContent}
          </div>
          <div className="col-span-3 mb-4">
            <h4 className="font-medium text-[17px] text-white">{t('service.footer.connect')}</h4>
            <ul className="[&_li]:leading-loose [&_li]:text-[15px] [&_li]:font-normal [&_a]:text-slate-400 [&_a]:transition-colors [&_a]:duration-300 [&_a:hover]:text-white">
              <li>
                <a href={facebookLink}>Facebook</a>
              </li>
              <li>
                <a href={twitterLink}>Twitter</a>
              </li>
              {blog}
            </ul>
          </div>
        </div>
        <hr className="border border-slate-600 mt-16 mb-4 hidden md:block" />
        <div className="text-center">
          <div className="mt-4 text-secondary">© LingualBox, Inc.</div>
        </div>
      </div>
    </footer>
  );
};

export default withTranslation('common')(FooterWithContents);
